import useApi from 'vl-common/src/hooks/useApi';
import React, { useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from 'antd';
import styled from '@emotion/styled';
import { NoteEditor } from 'vl-common/src/components/NoteEditor';
import { PatientNote, NoteWithReplies } from 'vl-common/src/components/PatientNote';
import useIsReferrerAdmin from 'vl-common/src/hooks/useIsReferrerAdmin';
import useIsReferrer from 'vl-common/src/hooks/useIsReferrer';
import { useTranslation } from 'react-i18next';

const NotesPanel = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const getNotesWithReplies = (notes: NoteWithReplies[]): NoteWithReplies[] => {
  const notesWithReplies: NoteWithReplies[] = [];

  notes.forEach((note) => {
    if (!note.parent_id) {
      // in the 'edit' mode
      // if note is the conversation starter
      // then 'Shared with clinicians' checkbox should be always visible
      note.isNoteConversationStarter = true;
    }
    const replies = notes
      .filter((n) => n.parent_id === note.note_id)
      // sort replies from the oldest to the latest
      .sort((lhs, rhs) => lhs.created_date.getTime() - rhs.created_date.getTime());

    replies.forEach((c) => {
      // in the 'edit' mode
      // if the parent note of current reply is shared with clinicians
      // then 'Shared with clinicians' checkbox should be visible for this reply
      c.isParentNoteClinVisible = note.clin_visible;
    });

    note.replies = replies;
    notesWithReplies.push(note);
  });

  // sort notes from the latest to the oldest
  return notesWithReplies.sort((lhs, rhs) => rhs.created_date.getTime() - lhs.created_date.getTime());
};

// A panel containing all the notes for a given patient (and case if case_id is specified).
export const PatientNotesPanel = ({ patient_guid, case_id, title = null, onMessagesLoaded = (_: number) => {} }) => {
  const { t } = useTranslation('common');
  const { Api } = useApi();
  const [isOpenAddNoteEditor, setOpenAddNoteEditor] = useState(false);
  const [currentNoteId, setCurrentNoteId] = useState(0);
  const isReferer = useIsReferrer();
  const isAxa = useIsReferrerAdmin() || isReferer;

  const { data: notes = [] } = useQuery({
    queryKey: ['getPatientNotes', case_id],
    queryFn: async () => {
      const res = await Api.notes.getPatientNotes(patient_guid, case_id);
      return res.data?.patient_notes ?? [];
    },
    enabled: !!case_id && !!patient_guid && currentNoteId === 0
  });

  const notesWithReplies = useMemo(() => getNotesWithReplies(notes), [notes]);
  const conversationStarters = useMemo(() => notesWithReplies.filter((n) => !n.parent_id), [notesWithReplies]);

  useEffect(() => {
    onMessagesLoaded(notes.length);
  }, [notes]);

  if (isAxa) {
    return null;
  }

  return (
    <NotesPanel>
      {title}
      {isOpenAddNoteEditor ? (
        <NoteEditor
          patient_guid={patient_guid}
          mode="add"
          onClosed={() => setOpenAddNoteEditor(false)}
          case_id={case_id}
        />
      ) : (
        <Button
          onClick={() => {
            setCurrentNoteId(0);
            setOpenAddNoteEditor(true);
          }}
          data-testid="add-conversation-button"
        >
          {t('New conversation')}
        </Button>
      )}

      {conversationStarters.map((n) => (
        <PatientNote
          key={n.note_id}
          patient_guid={patient_guid}
          currentNoteId={currentNoteId}
          setCurrentNoteId={(noteId) => {
            setCurrentNoteId(noteId);
            setOpenAddNoteEditor(false);
          }}
          note={n}
          case_id={case_id}
        />
      ))}
      {conversationStarters.length === 0 && <br />}
    </NotesPanel>
  );
};

export default PatientNotesPanel;
